import { makeStyles } from "@mui/styles";
import { Button } from "components/Button/Button";
import ElementLoader from "components/ElementLoader";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Popover } from "components/Popover/Popover";
import ImportLocationRemap, {
    buildRemapConfig,
    sourceLocationKey,
} from "containers/Resources/components/ImportSessionModal/ImportLocationRemap";
import ImportSessionPreview, {
    CHANGE_VALUE,
    changeValueField,
    describeLocations,
    isMissingChangeValue,
    locationProblems,
    parseChangeValue,
    problemKey,
    withAutoResolutions,
    wouldImportAnything,
} from "containers/Resources/components/ImportSessionModal/ImportSessionPreview";
import {
    ImportProblem,
    ImportResolution,
    ImportSessionConfig,
    ImportSourceLocation,
    StagedFile,
    useImportSession,
} from "containers/Resources/components/ImportSessionModal/useImportSession";
import { useEffect, useMemo, useState } from "react";

import "./ImportSessionModal.scss";

interface ImportSessionModalProps {
    /** The picked file to import. Non-null opens the modal; null keeps it closed. */
    file: StagedFile | null;
    /** Close the modal (clears the picked file in the parent). */
    onClose: () => void;
}

/** Cancel resets the import session, so it is locked while an apply is in flight. */
export const isImportCancelLocked = (phase: string): boolean => phase === "applying";

/**
 * Interactive, retryable import dialog. Replaces the old fire-and-forget
 * "stash file -> navigate to the logs screen -> POST /import?async=true" flow
 * with: upload once -> preflight problems + config -> resolve -> apply -> watch
 * the result -> retry against the same staged zip until clean -> success
 * summary. Built on the canonical `Popover` dialog shell per
 * `ui-design-principle-modals-dialogs` (24px body padding, mandatory bold
 * in-body header, no corner ×, space-between Cancel-left / confirm-right button
 * row using the shared `<Button>`/`<CancelButton>` with a single primary-blue
 * confirm) and `ui-design-principle-buttons`.
 */
export const ImportSessionModal = ({ file, onClose }: ImportSessionModalProps) => {
    const {
        phase,
        fileName,
        preview,
        error,
        hasApplied,
        refreshing,
        stageAndPreflight,
        refreshPreflight,
        applySession,
        reset,
    } = useImportSession();

    const [allowUpsert, setAllowUpsert] = useState(true);
    const [createMissingLocations, setCreateMissingLocations] = useState(false);
    // source-location key -> chosen target location name ("Import into" table)
    const [locationChoices, setLocationChoices] = useState<Record<string, string>>({});
    // problemKey -> chosen resolution id
    const [resolutions, setResolutions] = useState<Record<string, string>>({});
    // problemKey -> text typed for a "Set a value" (change-value) resolution
    const [changeValues, setChangeValues] = useState<Record<string, string>>({});

    // Stage + preflight whenever a new file is handed in.
    useEffect(() => {
        if (file) {
            setAllowUpsert(true);
            setCreateMissingLocations(false);
            setLocationChoices({});
            setResolutions({});
            setChangeValues({});
            stageAndPreflight(file);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    // Auto-apply the single functional resolution of every no-choice problem
    // (Margot 2026-10-08): the preview states such a problem's consequence as
    // plain text instead of a one-item dropdown, so the resolution must actually
    // be selected for the stated consequence to hold on Import. Preserves any
    // manual pick on a multi-option problem (see withAutoResolutions).
    useEffect(() => {
        const problems = preview?.problems ?? [];
        if (problems.length === 0) {
            return;
        }
        setResolutions((current) => withAutoResolutions(problems, current));
    }, [preview]);

    const handleClose = () => {
        reset();
        setResolutions({});
        setChangeValues({});
        setLocationChoices({});
        onClose();
    };

    const onResolutionChange = (problem: ImportProblem, resolutionId: string) => {
        const key = problemKey(problem.record_type, problem.record_name);
        setResolutions((current) => {
            const next = { ...current };
            if (resolutionId) {
                next[key] = resolutionId;
            } else {
                delete next[key];
            }
            return next;
        });
    };

    const onValueChange = (problem: ImportProblem, value: string) => {
        const key = problemKey(problem.record_type, problem.record_name);
        setChangeValues((current) => ({ ...current, [key]: value }));
    };

    // A chosen "Set a value" resolution without a value would leave the problem
    // blocking, so Import waits until each one has a value; the problem card
    // says so next to the empty input.
    const missingChangeValue = useMemo(
        () =>
            (preview?.problems ?? []).some((problem) => {
                const key = problemKey(problem.record_type, problem.record_name);
                return (
                    resolutions[key] === CHANGE_VALUE &&
                    isMissingChangeValue(changeValues[key])
                );
            }),
        [preview, resolutions, changeValues]
    );

    // Nothing left to import (every record skipped): Import stays disabled.
    const nothingToImport = useMemo(
        () => !!preview && !wouldImportAnything(preview.records ?? [], resolutions),
        [preview, resolutions]
    );

    const resolutionList: ImportResolution[] = useMemo(() => {
        const problems = preview?.problems ?? [];
        return problems
            .map((problem) => {
                const key = problemKey(problem.record_type, problem.record_name);
                const resolution = resolutions[key];
                if (!resolution) {
                    return null;
                }
                const entry: ImportResolution = {
                    record_type: problem.record_type,
                    record_name: problem.record_name,
                    resolution,
                };
                const field = changeValueField(problem);
                if (resolution === CHANGE_VALUE && field) {
                    entry.field = field;
                    entry.value = parseChangeValue(field, changeValues[key] ?? "");
                }
                return entry;
            })
            .filter((entry): entry is ImportResolution => entry !== null);
    }, [preview, resolutions, changeValues]);

    // The distinct missing locations (project/bundle) the "Create missing
    // target locations" option would create. Drives both (a) showing that
    // option only when it is actually relevant and (b) naming the specific
    // locations it would create (Margot, 2026-10-15): the error says "missing
    // reference" and the reference is a project/bundle, so without this it is
    // not clear the checkbox resolves that problem.
    const missingLocations = useMemo(
        () => locationProblems(preview?.problems ?? []),
        [preview]
    );

    const sourceLocations = useMemo(
        () => preview?.source_locations ?? [],
        [preview]
    );

    // The session config for the current choices. The remap is derived from the
    // "Import into" table; an untouched table sends no remap.
    const buildConfig = (overrides: {
        allowUpsert?: boolean;
        createMissingLocations?: boolean;
        locationChoices?: Record<string, string>;
    }): ImportSessionConfig => {
        const config: ImportSessionConfig = {
            allow_upsert: overrides.allowUpsert ?? allowUpsert,
            create_missing_locations:
                overrides.createMissingLocations ?? createMissingLocations,
        };
        const remap = buildRemapConfig(
            sourceLocations,
            overrides.locationChoices ?? locationChoices
        );
        if (remap) {
            config.remap = remap;
        }
        return config;
    };

    const onLocationChange = (location: ImportSourceLocation, targetName: string) => {
        const key = sourceLocationKey(location);
        const current = locationChoices[key] ?? location.target_name;
        if (targetName === current) {
            // Same target as before: nothing can change, so no re-check.
            return;
        }
        const next = { ...locationChoices, [key]: targetName };
        setLocationChoices(next);
        refreshPreflight(buildConfig({ locationChoices: next }));
    };

    const onApply = () => {
        applySession(buildConfig({}), resolutionList);
    };

    const busyLabel = phase === "staging" ? "Uploading…" : "Checking for problems…";

    const imported = preview?.imported ?? {};
    const importedTotal = Object.values(imported).reduce(
        (sum, ids) => sum + ids.length,
        0
    );

    const rootStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
    };
    const paperStyle = {
        width: "760px",
        maxWidth: "92vw",
        maxHeight: "85vh",
        overflow: "visible",
    };
    const useStyles = makeStyles(() => ({ rootStyle, paperStyle }));
    const classes = useStyles();

    return (
        <Popover
            isOpen={file !== null}
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="import-session-modal__popover"
            contentClassName="import-session-modal__content"
            anchorReference="none"
            closeOnInsideClick={false}
            closeOnOutsideClick={false}
        >
            <div className="import-session-modal__header">
                Import {fileName ? `"${fileName}"` : "resources"}
            </div>

            <div className="import-session-modal__body">
                {(phase === "staging" || phase === "preflighting") && (
                    <div className="import-session-modal__loading">
                        <ElementLoader />
                        <span>{busyLabel}</span>
                    </div>
                )}

                {phase === "error" && (
                    <div className="import-session-modal__error">
                        <b>Import could not be prepared.</b>
                        <div>{error}</div>
                    </div>
                )}

                {/* review / applying / done all keep the SAME records list on
                    screen (Margot 2026-10-15). During applying the busy state is
                    shown only on the Import button (loading) — not a full-screen
                    loader that would hide the list. On done the preview gains a
                    per-record Imported/Skipped Result column. */}
                {(phase === "review" || phase === "applying" || phase === "done") &&
                    preview && (
                        <>
                            {phase === "review" &&
                                hasApplied &&
                                (preview.summary?.blocking ?? 0) > 0 && (
                                    <div className="import-session-modal__retry-banner">
                                        The import did not complete — resolve the
                                        blocking problems below, then import again.
                                    </div>
                                )}
                            {phase === "applying" && (
                                <div className="import-session-modal__refreshing">
                                    Importing… nothing is changed until this finishes.
                                </div>
                            )}
                            {error && phase !== "done" && (
                                <div className="import-session-modal__error">{error}</div>
                            )}
                            <ImportSessionPreview
                                preview={preview}
                                resolutions={resolutions}
                                onResolutionChange={onResolutionChange}
                                values={changeValues}
                                onValueChange={onValueChange}
                                mode={
                                    phase === "done"
                                        ? "done"
                                        : phase === "applying"
                                        ? "applying"
                                        : "review"
                                }
                            />
                            {phase === "review" && (
                                <div className="import-session-modal__config">
                                    <ImportLocationRemap
                                        locations={sourceLocations}
                                        choices={Object.fromEntries(
                                            sourceLocations.map((location) => [
                                                sourceLocationKey(location),
                                                locationChoices[
                                                    sourceLocationKey(location)
                                                ] ?? location.target_name,
                                            ])
                                        )}
                                        disabled={refreshing}
                                        onChange={onLocationChange}
                                    />
                                    <label className="import-session-modal__config-row">
                                        <input
                                            type="checkbox"
                                            checked={allowUpsert}
                                            disabled={refreshing}
                                            onChange={(event) => {
                                                const next = event.target.checked;
                                                setAllowUpsert(next);
                                                refreshPreflight(
                                                    buildConfig({ allowUpsert: next })
                                                );
                                            }}
                                        />
                                        <span>Update existing records (upsert)</span>
                                    </label>
                                    {/* Show the "create missing locations"
                                        option only when a referenced project or
                                        bundle is actually missing (or it is
                                        already enabled, so it can still be turned
                                        off). An always-shown toggle that changes
                                        nothing but triggers a slow re-check reads
                                        as broken (Margot, 2026-10-15). */}
                                    {(missingLocations.length > 0 ||
                                        createMissingLocations) && (
                                        <div className="import-session-modal__config-option">
                                            <label className="import-session-modal__config-row">
                                                <input
                                                    type="checkbox"
                                                    checked={createMissingLocations}
                                                    disabled={refreshing}
                                                    onChange={(event) => {
                                                        const next =
                                                            event.target.checked;
                                                        setCreateMissingLocations(
                                                            next
                                                        );
                                                        refreshPreflight(
                                                            buildConfig({
                                                                createMissingLocations:
                                                                    next,
                                                            })
                                                        );
                                                    }}
                                                />
                                                <span>
                                                    Create missing target locations
                                                </span>
                                            </label>
                                            {missingLocations.length > 0 && (
                                                <div className="import-session-modal__config-hint">
                                                    Will create:{" "}
                                                    {describeLocations(
                                                        missingLocations
                                                    )}
                                                    .
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {refreshing && (
                                        <div className="import-session-modal__refreshing">
                                            Re-checking problems…
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                {/* Fallback: a successful apply that produced no record rows
                    (e.g. a build-phase result with records: []). Keep the plain
                    aggregate so the done state is never empty. */}
                {phase === "done" && !preview && (
                    <div className="import-session-modal__success">
                        <div className="import-session-modal__success-title">
                            Import complete
                        </div>
                        <div>
                            {importedTotal} record{importedTotal === 1 ? "" : "s"} imported
                        </div>
                    </div>
                )}
            </div>

            <div className="import-session-modal__buttons">
                {phase === "done" ? (
                    <>
                        <span />
                        <Button
                            className="import-session-modal__confirm"
                            variation="primary"
                            onClick={handleClose}
                            label="Done"
                        />
                    </>
                ) : (
                    <>
                        {/* Cancel resets the session; locked while an apply is in flight. */}
                        <CancelButton onClick={handleClose} disabled={isImportCancelLocked(phase)} />
                        <Button
                            className="import-session-modal__confirm"
                            variation="primary"
                            disabled={
                                phase !== "review" ||
                                refreshing ||
                                missingChangeValue ||
                                nothingToImport
                            }
                            loading={phase === "applying"}
                            onClick={onApply}
                            label={
                                phase === "applying"
                                    ? "Importing…"
                                    : hasApplied
                                    ? "Import again"
                                    : "Import"
                            }
                        />
                    </>
                )}
            </div>
        </Popover>
    );
};

export default ImportSessionModal;
